import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule, Location, UpperCasePipe } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DATE_FORMAT } from 'src/app/shared/constants';
import {
    DxButtonModule,
    DxDateBoxModule,
    DxFormModule,
    DxLoadPanelModule,
    DxNumberBoxModule,
    DxRadioGroupModule,
    DxScrollViewModule,
    DxSelectBoxModule,
    DxTagBoxModule,
    DxTextAreaModule,
    DxTextBoxModule,
    DxToolbarModule,
    DxValidationGroupComponent,
    DxValidationGroupModule,
    DxValidatorModule,
} from 'devextreme-angular';
import { DxScrollViewComponent } from 'devextreme-angular/ui/scroll-view';
import {
    AuthService,
    PermissionType,
    AccessType,
    DataSources,
    InvoicingClient,
    CreditRequestDto,
    CreditRequestStatus,
    CreditRequestAction,
    CurrencyCode,
    VendorInfoDto,
    BillToInfoDto,
    EmployeesClient,
    FinancialDocumentItemDto,
    UpdateCreditRequestCommand,
    CreateCreditRequestCommand,
} from '@services';
import notify from 'devextreme/ui/notify';
import { DxCheckBoxModule } from 'devextreme-angular/ui/check-box';
import EnumUtils from 'src/app/shared/enum-utils';
import { FileUtils } from 'src/app/shared/file-utils';
import { FormatPhonePipe } from 'src/app/pipes/format-phone.pipe';
import { InvoiceDetailsItemsComponent } from 'src/app/pages/Invoicing/components/invoice-details-items.component/invoice-details-items.component';
import * as AspNetData from "devextreme-aspnet-data-nojquery";
import DataSource from 'devextreme/data/data_source';
import { CreditRequestDetailsToolbarComponent } from './toolbar/credit-request-details-toolbar.component';
import { CreditRequestStatusPipe } from 'src/app/pipes/credit-requests-status.pipe';
import { NotesComponent, NoteType } from 'src/app/components/library/notes/notes.component';

@Component({
    selector: 'app-credit-request-details',
    imports: [
        CommonModule,
        DxToolbarModule,
        DxButtonModule,
        DxLoadPanelModule,
        DxValidationGroupModule,
        DxTextBoxModule,
        DxNumberBoxModule,
        DxValidatorModule,
        DxSelectBoxModule,
        DxTagBoxModule,
        DxDateBoxModule,
        DxScrollViewModule,
        DxFormModule,
        DxRadioGroupModule,
        DxTextAreaModule,
        DxCheckBoxModule,
        FormatPhonePipe,
        InvoiceDetailsItemsComponent,
        CreditRequestDetailsToolbarComponent,
        RouterLink,
        CreditRequestStatusPipe,
        NotesComponent,
    ],
    providers: [UpperCasePipe, CreditRequestStatusPipe],
    templateUrl: './credit-request-details.component.html',
})
export class CreditRequestDetailsComponent implements OnInit {
    @Input() number: string | null = null;
    @ViewChild('validationGroup', { static: true }) validationGroup!: DxValidationGroupComponent;
    @ViewChild(DxScrollViewComponent, { static: false }) scrollView?: DxScrollViewComponent;
    @ViewChild(NotesComponent) notesComponent: NotesComponent;

    private route = inject(ActivatedRoute);
    private location = inject(Location);
    private router = inject(Router);
    private auth = inject(AuthService);
    private dataSources = inject(DataSources);
    private invoicingClient = inject(InvoicingClient);
    private employeesClient = inject(EmployeesClient);
    private upperCasePipe = inject(UpperCasePipe);

    protected readonly PermissionType = PermissionType;
    protected readonly NoteType = NoteType;

    currencyOptions = EnumUtils.getEnumOptions(CurrencyCode, (v) =>
        this.upperCasePipe.transform(v),
    );

    serviceTypeDataSource = this.dataSources.availableServiceTypeOptions();
    invoiceOptionsDataSource: AspNetData.CustomStore | [] = [];
    vendorDataSource = this.dataSources.activeVendorOptions();
    billToDataSource: DataSource = this.buildBillToDataSource();
    dateFormat = DATE_FORMAT;
    minDueDate = new Date();
    isLoading: boolean = false;
    isSubmitting: boolean = false;
    isExporting: boolean = false;
    isReadOnly = false;
    creditRequest: CreditRequestDto = new CreditRequestDto();
    vendor: VendorInfoDto | null = null;
    billTo: BillToInfoDto | null = null;
    pendingNoteAction: CreditRequestAction | null = null;

    // When viewing, feed the Bill To box the stored value so it always renders, even if the current
    // user's site scope would exclude it. Editing uses the scoped remote options.
    private buildBillToDataSource(): DataSource {
        if (this.isReadOnly) {
            return new DataSource({
                key: 'id',
                store: this.creditRequest?.billToClientId
                    ? [{ id: this.creditRequest.billToClientId, name: this.creditRequest.billToName }]
                    : [],
            });
        }

        return new DataSource({
            store: this.dataSources.billToOptions(PermissionType.CreditRequests),
            paginate: true,
        });
    }
    actionsRequiringNote: CreditRequestAction[] = [
        CreditRequestAction.Cancel,
        CreditRequestAction.BackToAP,
    ];

    ngOnInit(): void {
        this.number = this.route.snapshot.paramMap.get('number');
        if (this.number) {
            this.loadCreditRequest();
        } else {
            this.initializeNewCreditRequest();
        }
    }

    get canViewNotes(){
        return this.auth.checkPermission(PermissionType.CreditRequestNotes, AccessType.ViewOnly);
    }

    get canCreateNotes(){
        return this.auth.checkPermission(PermissionType.CreditRequestNotes, AccessType.Manage);
    }

    get canCreateInternalNotes(){
        return this.auth.checkPermission(PermissionType.CreditRequestInternalNotes, AccessType.Manage);
    }

    protected getBadgeClass(status: CreditRequestStatus | undefined): string {
        return EnumUtils.getCreditRequestStatusBadgeClass(status);
    }

    private initializeNewCreditRequest() {
        this.creditRequest = new CreditRequestDto();
        this.creditRequest.items = [FinancialDocumentItemDto.fromJS({ quantity: 1, description: '', unitPrice: 0.00, taxRate: 0 })];
        this.creditRequest.subTotal = 0;
        this.creditRequest.taxAmount = 0;
        this.creditRequest.totalAmount = 0;
        this.setVendor();

        this.isReadOnly = !this.auth.checkPermission(PermissionType.CreditRequestDetails, AccessType.Edit);
        this.billToDataSource = this.buildBillToDataSource();
    }

    private loadCreditRequest() {
        this.isLoading = true;
        this.invoicingClient.getCreditRequest(this.number!)
            .subscribe({
                next: async (data) => {
                    this.creditRequest = data;
                    this.isLoading = false;
                    this.vendor = { serviceTypeId: data.serviceTypeId } as VendorInfoDto;
                    this.updateVendorInfo(false, true);
                    this.updateBillToInfo();
                    this.onServiceTypeChanged();

                    const canEdit = this.auth.checkPermission(PermissionType.CreditRequestDetails, AccessType.Manage)
                        || (this.creditRequest.status === CreditRequestStatus.Draft && this.auth.checkPermission(PermissionType.CreditRequestDetails, AccessType.Edit));

                    this.isReadOnly = !canEdit;
                    this.billToDataSource = this.buildBillToDataSource();
                },
                error: () => {
                    notify('Failed to load credit request', 'error', 3000);
                    this.isLoading = false;
                    this.location.back();
                },
            });
    }

    protected onInvoiceChanged(e: { selectedItem?: { billToClientId?: string } }) {
        if (this.number) return;

        if (e.selectedItem) {
            this.creditRequest.billToClientId = e.selectedItem.billToClientId;
        }
    }

    protected onServiceTypeChanged() {
        this.vendorDataSource = this.dataSources.activeVendorOptions(this.creditRequest.serviceTypeId);
        if(this.vendor?.serviceTypeId !== this.creditRequest.serviceTypeId) {
            this.creditRequest.vendorId = undefined;
        }
    }

    private setVendor() {
        if(this.auth.userId !== null) {
            this.employeesClient.getProfileVendor(this.auth.userId).subscribe({
                next: (data) => {
                    if(data.vendors?.length == 1){
                        this.creditRequest.vendorId = data.vendors[0]
                    }
                },
                error: () => {
                    this.isLoading = false;
                    notify('Failed to load vendors', 'error', 3000);
                }
            });
        }
    }

    protected updateVendorInfo(applyGlCodeDefaults: boolean, preserveInvoice: boolean = false) {
        if (this.creditRequest.vendorId) {
            this.invoicingClient.getVendorInfo(this.creditRequest.vendorId).subscribe({
                next: (vendor) => {
                    this.vendor = vendor;

                    if(this.creditRequest.serviceTypeId === undefined) {
                        this.creditRequest.serviceTypeId = vendor.serviceTypeId;
                    }

                    if (!preserveInvoice) {
                        this.creditRequest.invoiceId = undefined;
                    }

                    if (applyGlCodeDefaults) {
                        this.creditRequest.items?.forEach(item => {
                            item.glCodeId = vendor.glCodeId;
                            item.glCodeName = vendor.glCodeName;
                            item.siteId = vendor.glCodeSiteId;
                        });
                    }

                    this.invoiceOptionsDataSource = this.dataSources.invoiceOptions(this.creditRequest.vendorId!, this.creditRequest.invoiceId);
                },
                error: () => {
                    notify('Failed to load vendor details', 'error', 3000);
                    this.vendor = null;
                },
            });

        } else {
            this.vendor = null;
        }
    }

    protected updateBillToInfo() {
        if (this.creditRequest.billToClientId) {
            this.invoicingClient.getBillToInfo(this.creditRequest.billToClientId).subscribe({
                next: (billTo) => {
                    this.billTo = billTo;
                },
                error: () => {
                    this.billTo = null;
                },
            });
        } else {
            this.billTo = null;
        }
    }

    async onDateChanged() {
        if (!this.creditRequest.issueDate) return;

        this.minDueDate = this.creditRequest.issueDate;

        this.updateCurrency();
    }

    updateCurrency() {
        if (this.creditRequest.currency == CurrencyCode.USD && this.creditRequest.issueDate) {
            const conversionDate = new Date(this.creditRequest.issueDate);
            conversionDate.setMonth(conversionDate.getMonth() - 1);

            this.invoicingClient
                .getConversionRate(conversionDate)
                .subscribe({
                    next: (conversionRate) => {
                        this.creditRequest.conversionRate = conversionRate;
                    },
                    error: () => {
                        notify(
                            'Failed to load conversion rate',
                            'error',
                            3000,
                        );
                        this.creditRequest.conversionRate =
                            undefined;
                    },
                });
        } else {
            this.creditRequest.conversionRate = undefined;
        }
    }

    onSaveClick(action: CreditRequestAction) {
        if(this.actionsRequiringNote.includes(action))
        {
            this.pendingNoteAction = action;
            this.notesComponent.openCreatePopup();

        } else{
            this.save(action);
        }
    }

    onNoteSaved() {
        if (!this.pendingNoteAction)
        {
            return;
        }

        const action = this.pendingNoteAction;
        this.pendingNoteAction = null;
        this.save(action);
    }

    onNoteCanceled() {
        this.pendingNoteAction = null;
    }

    async save(action: CreditRequestAction) {
        const result = this.validationGroup.instance.validate();

        if (!result.isValid) {
            notify(
                {
                    message: 'Please fix validation errors',
                    position: 'bottom center',
                },
                'error',
                3000,
            );
            return;
        }

        this.isSubmitting = true;

        const command = this.number ?
            new UpdateCreditRequestCommand({
                ...this.creditRequest,
                number: this.number,
                action: action}) :
            new CreateCreditRequestCommand({ ...this.creditRequest, action: action });

        const request$ = this.number ?
            this.invoicingClient.updateCreditRequest(command) :
            this.invoicingClient.createCreditRequest(command);

        request$.subscribe({
            next: async (id) => {
                if (this.number === null && this.notesComponent) {
                    await this.notesComponent.saveQueuedNotes(id);
                }

                notify(
                    this.number
                        ? 'Credit request updated successfully'
                        : 'Credit request created successfully',
                    'success',
                    3000,
                );
                this.isSubmitting = false;
                if (this.number) {
                    this.loadCreditRequest();
                } else {
                    await this.router.navigate(['invoicing/credit-requests/details', id], {
                        replaceUrl: true,
                    });
                }
            },
            error: (errors: Array<any>) => {
                const msg = errors?.[0]?.message ||
                    (this.number
                        ? 'Failed to update credit request'
                        : 'Failed to create credit request');
                notify(msg, 'error', 3000);
                this.isSubmitting = false;
            },
        });
    }

    exportPdf() {
        this.isExporting = true;
        this.invoicingClient.exportCreditRequestPdf(this.number!).subscribe({
            next: (file) => {
                FileUtils.downloadFile(file);
                this.isExporting = false;
            },
            error: () => {
                notify('Failed to export credit request', 'error', 3000);
                this.isExporting = false;
            },
        });
    }

    cancel() {
        this.location.back();
    }
}
